/**
 * rother3 companion — the horizontal scroll indicator for a wide table.
 *
 * ## Why this exists
 *
 * The Listing page's table is **2,232px wide inside a 1,206px wrapper** at 137 businesses. More
 * than a third of it is off-screen, and the browser's own scrollbar is the only thing saying so.
 *
 * That is not good enough, for four reasons that each point somewhere different:
 *
 * 1. **The scrollbar is invisible on macOS until you scroll.** A page that only reveals its
 *    overflow affordance on interaction is telling a first-time visitor the table ends where the
 *    viewport ends, which is a false claim about the data.
 * 2. **It sits at the bottom of a tall element.** The Listing page shows 25 rows. The scrollbar
 *    is below all of them, so by the time a reader reaches it they have already concluded the
 *    table ends.
 * 3. **It is not keyboard-reachable.** `overflow-x: auto` gives no focus stop. A keyboard user
 *    has no way to reach column nineteen except by arrowing onto a cell, which does not scroll
 *    a container in every browser.
 * 4. **It disappears during page scroll.** Sticky headers stay; the scrollbar of a nested
 *    scroller does not.
 *
 * The indicator goes at the **top** of the table, directly under the sticky header row, because
 * that is the only place it can be seen at the moment the reader needs it — while their eyes are
 * on the column headings, deciding which column to scroll to.
 *
 * ## What it shows
 *
 * A thumb whose width is the **visible fraction** and whose position is the **scroll fraction**.
 * Both, because either alone is incomplete: a full-width bar that slides around tells you where
 * you are but not how much is hidden, and a proportional bar that never moves tells you the
 * proportion but not your position in it.
 *
 * It disappears entirely when the table fits. An indicator over a table with nothing to scroll
 * is noise, and worse, it implies there is more to find.
 *
 * ## Accessibility
 *
 * The bar is `aria-hidden` and purely visual. The *keyboard* path is a real `<button>` pair that
 * scrolls the table by a measured amount, which is what makes this an affordance rather than a
 * decoration. Focus is never trapped: the buttons are ordinary tab stops inside the page's own
 * order.
 */
const clamp = (value, low, high) => Math.min(Math.max(value, low), high);

/** Fraction of the table's width moved per press. Matches what a trackpad swipe feels like. */
const PAGE_FRACTION = 0.9;

/**
 * Build the indicator, mounted above a table's wrapper.
 *
 * Returns the DOM node to insert. Kept as a function rather than markup in `renderTable` because
 * the markup belongs with the renderer and the *behaviour* needs `document`, which is the split
 * that lets this be tested in jsdom without a layout engine.
 *
 * @param {object} [options]
 * @param {string} [options.label] - accessible name for the scroll region
 * @returns {HTMLElement}
 */
export function buildScrollIndicator(options = {}) {
  const el = document.createElement('div');
  el.className = 'fam-scroll';
  el.hidden = true;

  // The buttons scroll the table; the bar between them reports where it is. One row, one tab
  // stop each, and the bar carries no semantics of its own.
  el.innerHTML = `
    <button type="button" class="fam-scroll-btn fam-scroll-btn--left" data-scroll="left" aria-label="${options.leftLabel ?? 'Scroll the table left'}">
      <span aria-hidden="true">&#8249;</span>
    </button>
    <div class="fam-scroll-track" aria-hidden="true">
      <div class="fam-scroll-thumb"></div>
    </div>
    <button type="button" class="fam-scroll-btn fam-scroll-btn--right" data-scroll="right" aria-label="${options.rightLabel ?? 'Scroll the table right'}">
      <span aria-hidden="true">&#8250;</span>
    </button>`;

  return el;
}

/**
 * Wire an indicator to its table.
 *
 * ## Why this is idempotent and re-callable
 *
 * The table is re-rendered on every filter keystroke and every page change, which replaces the
 * wrapper element entirely. A listener attached once would point at a detached node and silently
 * stop working — a bug that appears only after the reader has typed something, which is exactly
 * when the scrollbar matters most. So `mount` is called after **every** render, and it detaches
 * the previous listeners before attaching new ones.
 *
 * @param {HTMLElement} wrapper - the scrolling element
 * @param {HTMLElement} indicator - from `buildScrollIndicator`
 * @param {() => void} [detach] - teardown for the previous mount, returned by this function
 */
export function mountScrollIndicator(wrapper, indicator, detach) {
  if (typeof detach === 'function') {detach();}
  if (!wrapper || !indicator) {return () => {}}

  const thumb = indicator.querySelector('.fam-scroll-thumb');
  const left = indicator.querySelector('[data-scroll="left"]');
  const right = indicator.querySelector('[data-scroll="right"]');
  const head = document.querySelector('.fam-head-row');

  /**
   * Keep the sticky header horizontally aligned with the body.
   *
   * The header is a second `<table>` outside the scroller (see `renderTable` for why), so nothing
   * links its horizontal position to the body's. Without this it sits at scroll position 0 while
   * the data slides under it, and a reader comparing column twelve against column three is
   * reading two different columns.
   *
   * **The first column is not part of the shift.** It is the one cell that must not move, and a
   * transform on an ancestor moves every descendant, `position: sticky` included — sticky offsets
   * are computed against the *transformed* containing block, so `left: 0` on the name cell lands
   * it at the shifted edge rather than the viewport edge. Measured: at 658px of scroll the
   * header's "Business" cell sat at -621px, off the left of the screen.
   *
   * So the shift goes on `.fam-head-row`, the flex row **inside** the sticky first column's
   * neighbour — the narrowest element that can carry it without dragging the sticky cell along.
   *
   * `translate3d`, not `translateX`: it keeps the shift on the compositor, which matters because
   * this runs on every scroll frame of a table with 25 rows and 20 columns.
   *
   * A margin was tried first and does not work: the row lives inside a `<td>` of a
   * `table-layout: fixed` table, and a table cell's margin does not move its content.
   */
  const syncHeader = () => {
    if (!head) {return;}
    head.style.transform = `translate3d(${-wrapper.scrollLeft}px, 0, 0)`;
  };

  /**
   * The thumb is a share of the *track*, not of the table.
   *
   * `clientWidth / scrollWidth` is the visible fraction, and the thumb is drawn at that fraction
   * of the track. Computing it against the scroll width instead — the obvious version — makes
   * the thumb's travel exceed the track by exactly the hidden amount, so at the far right it
   * hangs off the end.
   */
  const update = () => {
    syncHeader();
    const scrollW = wrapper.scrollWidth;
    const clientW = wrapper.clientWidth;
    const maxScroll = scrollW - clientW;

    if (maxScroll <= 1) {
      // Nothing hidden. Hide the whole affordance rather than showing a full-width bar that
      // implies there is more.
      indicator.hidden = true;
      if (head) {head.style.transform = 'translate3d(0, 0, 0)';}
      return;
    }

    indicator.hidden = false;
    const visibleFraction = clamp(clientW / scrollW, 0, 1);
    const scrolled = maxScroll > 0 ? clamp(wrapper.scrollLeft / maxScroll, 0, 1) : 0;

    thumb.style.width = `${visibleFraction * 100}%`;
    thumb.style.left = `${scrolled * (1 - visibleFraction) * 100}%`;

    // The buttons disable rather than hide at the ends. Hiding them shifts the track sideways as
    // you reach an end, which is the sort of movement that makes a control feel like it moved
    // on its own.
    left.disabled = wrapper.scrollLeft <= 0;
    right.disabled = wrapper.scrollLeft >= maxScroll - 1;

    // Which edges have more content. Used for the edge shadows, which are a second, non-focusable
    // cue for the same fact.
    wrapper.classList.toggle('fam-scrollable-left', wrapper.scrollLeft > 1);
    wrapper.classList.toggle('fam-scrollable-right', wrapper.scrollLeft < maxScroll - 1);
  };

  /**
   * Scroll by a measured amount, not by pixels.
   *
   * `clientWidth * 0.9` lands the new position on a column boundary far more often than a fixed
   * 200px would, because column widths here vary from 60px to 200px. It also guarantees a press
   * always moves, even on a table whose total overflow is smaller than any fixed step — which is
   * the bug a hard-coded step produces on a table that barely overflows.
   */
  const scrollBy = (direction) => {
    const step = Math.max(120, wrapper.clientWidth * PAGE_FRACTION);
    wrapper.scrollBy({ left: direction === 'left' ? -step : step, behavior: 'smooth' });
  };

  const onClick = (e) => {
    const button = e.target.closest('[data-scroll]');
    if (!button) {return;}
    scrollBy(button.dataset.scroll);
  };

  indicator.addEventListener('click', onClick);
  wrapper.addEventListener('scroll', update, { passive: true });

  // A resize can turn a scrollable table into a fitting one and back, and the thumb's width is a
  // fraction of a box that just changed. `ResizeObserver` rather than `window.resize` because the
  // wrapper also changes width when the page's own layout does, with no window event at all.
  const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(update) : null;
  if (observer) {observer.observe(wrapper);}

  // Measure after the browser has laid the table out. Called synchronously it reads the width the
  // wrapper had *before* the table was inserted, which is the full page width, and the indicator
  // reports "nothing hidden" on a table that hides half its columns.
  update();
  requestAnimationFrame(update);

  return () => {
    indicator.removeEventListener('click', onClick);
    wrapper.removeEventListener('scroll', update);
    if (observer) {observer.disconnect();}
  };
}

/**
 * Make the scrolling region itself reachable.
 *
 * A `tabindex="0"` region is the only thing that lets a keyboard user scroll a container. Without
 * it the arrow keys move between *cells* and the container follows only where a focused cell
 * already is, so column nineteen is unreachable when the reader never focuses a cell in it.
 *
 * Set on the wrapper, not on the table: the table's own semantics must stay clean for assistive
 * tech, and a `<table tabindex>` is announced as an interactive table.
 *
 * @param {HTMLElement} wrapper
 * @param {string} [label]
 */
export function makeScrollableRegionFocusable(wrapper, label = 'Comparison table, scrollable') {
  if (!wrapper) {return;}
  wrapper.tabIndex = 0;
  wrapper.setAttribute('role', 'region');
  wrapper.setAttribute('aria-label', label);
}